import { Component, EventEmitter, Output, inject } from '@angular/core';
import {
    SignalValueDescriptionRow,
    createNewValueDescriptionRow,
    createRowsFromAnchors,
    createValueDescriptionAnchorPayload,
    createValueDescriptionVersionPayload,
    getDescriptionText,
    getRowsToSave,
    getValueDescriptionValidationMessage,
    parseRowValue,
} from './signal-value-description-table.utils';
import {
    ValueDescriptionAnchor,
    ValueDescriptionVersion,
} from 'src/models/anchor-and-version/value-description.model';
import { ConfigState } from 'src/store/reducers/config.reducer';
import { FormsModule } from '@angular/forms';
import { NotificationService } from 'src/services/notification.service';
import { PendingChangesService } from 'src/services/pending-changes-service/pending-changes.service';
import { SignalAnchor } from 'src/models/anchor-and-version/signal.model';
import { Store } from '@ngrx/store';
import { ValueDescriptionService } from 'src/services/anchor-and-version-services/value-description.service';
import { int64Validator } from 'src/validators/integer.validator';
import { refreshPendingChangesCount } from 'src/utils/pending-changes-count.util';

// PrimeNG
// eslint-disable-next-line sort-imports
import { ButtonModule } from 'primeng/button';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { ConfirmationService } from 'primeng/api';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';

@Component({
    selector: 'app-signal-value-description-table',
    templateUrl: './signal-value-description-table.component.html',
    imports: [
        FormsModule,
        ButtonModule,
        ConfirmPopupModule,
        InputTextModule,
        TableModule,
        ToggleSwitchModule,
        TooltipModule,
    ],
})
export class SignalValueDescriptionTableComponent {
    @Output() public rowsChanged = new EventEmitter<void>();

    public rows: SignalValueDescriptionRow[] = [];
    public showValidationMessage = false;

    private confirmationService = inject(ConfirmationService);
    private notificationService = inject(NotificationService);
    private pendingChangesService = inject(PendingChangesService);
    private rowCounter = 0;
    private signalAnchor?: SignalAnchor;
    private store = inject<Store<{ config: ConfigState }>>(Store);
    private valueDescriptionService = inject(ValueDescriptionService);

    public addRow(): void {
        this.rows = [...this.rows, createNewValueDescriptionRow(`new-${++this.rowCounter}`)];
        this.rowsChanged.emit();
    }

    public confirmRemoveUnsavedRow(row: SignalValueDescriptionRow, event: Event): void {
        if (!row.isNew) return;

        this.confirmationService.confirm({
            target: event.target as EventTarget,
            key: 'removeValueDescriptionRowPopup',
            message: 'Remove this value description row?',
            header: 'Confirm remove',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Remove',
            rejectLabel: 'Cancel',
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-text',
            rejectVisible: true,
            accept: () => this.removeUnsavedRow(row),
        });
    }

    public getDescriptionText(row: SignalValueDescriptionRow): string {
        return getDescriptionText(row);
    }

    public getValidationMessage(): string | undefined {
        return getValueDescriptionValidationMessage(this.rows);
    }

    public getVisibleValidationMessage(): string | undefined {
        return this.showValidationMessage ? this.getValidationMessage() : undefined;
    }

    public isValueInvalid(row: SignalValueDescriptionRow): boolean {
        if (!row.value || int64Validator({ value: row.value })) return true;

        const value = BigInt(row.value);
        return this.rows.some(
            (other) =>
                other !== row &&
                !!other.value &&
                !int64Validator({ value: other.value }) &&
                BigInt(other.value) === value
        );
    }

    public async load(
        signalAnchor?: SignalAnchor,
        copyFromSignalAnchorKey?: number
    ): Promise<void> {
        this.rowCounter = 0;
        this.showValidationMessage = false;
        this.signalAnchor = signalAnchor;

        const sourceKey = copyFromSignalAnchorKey ?? signalAnchor?.key;
        if (!sourceKey) {
            this.setRowsFromAnchors([]);
            return;
        }

        await this.loadBySignalAnchorKey(sourceKey);
        if (copyFromSignalAnchorKey !== undefined) {
            this.rows = this.rows.map((row) => ({
                ...createNewValueDescriptionRow(`new-${++this.rowCounter}`),
                comment: row.comment,
                description: row.description,
                isActive: row.isActive,
                name: row.name,
                value: row.value,
            }));
        }
    }

    public onDescriptionChange(row: SignalValueDescriptionRow, value: string): void {
        row.description = value ?? '';
        row.name = row.description;
        this.rowsChanged.emit();
    }

    public onIsActiveChange(row: SignalValueDescriptionRow, value: boolean): void {
        row.isActive = Boolean(value);
        this.rowsChanged.emit();
    }

    public onRowFieldBlur(): void {
        this.showValidationMessage = true;
    }

    public onValueChange(row: SignalValueDescriptionRow, value: unknown): void {
        row.value = parseRowValue(value);
        this.rowsChanged.emit();
    }

    public async save(signalAnchorKey: number, changeRequestKey: number): Promise<void> {
        this.showValidationMessage = true;
        const validationMessage = this.getValidationMessage();
        if (validationMessage) {
            throw new Error(validationMessage);
        }

        const { newRows, changedRows } = getRowsToSave(this.rows);
        if (newRows.length === 0 && changedRows.length === 0) {
            return;
        }

        await Promise.all([
            newRows.length > 0
                ? this.valueDescriptionService.createAnchors(
                      newRows.map((row) =>
                          createValueDescriptionAnchorPayload(
                              row,
                              signalAnchorKey,
                              changeRequestKey
                          )
                      ) as unknown as ValueDescriptionAnchor[]
                  )
                : Promise.resolve([] as ValueDescriptionAnchor[]),
            changedRows.length > 0
                ? this.valueDescriptionService.createVersions(
                      changedRows.map((row) =>
                          createValueDescriptionVersionPayload(row, row.anchorKey, changeRequestKey)
                      ) as unknown as ValueDescriptionVersion[]
                  )
                : Promise.resolve([] as ValueDescriptionVersion[]),
        ]);

        this.notificationService.showSuccess('Value descriptions saved successfully');

        try {
            await refreshPendingChangesCount(this.store, this.pendingChangesService);
        } catch (error) {
            this.notificationService.handleError(
                'Value descriptions were saved, but pending changes count could not be refreshed',
                error
            );
        }

        await this.loadBySignalAnchorKey(signalAnchorKey);
    }

    private async loadBySignalAnchorKey(signalAnchorKey: number): Promise<void> {
        try {
            this.setRowsFromAnchors(
                await this.valueDescriptionService.getBySignal(signalAnchorKey)
            );
        } catch (error) {
            this.notificationService.handleError('Failed to load value descriptions', error);
            this.setRowsFromAnchors([]);
        }
    }

    private removeUnsavedRow(row: SignalValueDescriptionRow): void {
        this.rows = this.rows.filter((currentRow) => currentRow.rowId !== row.rowId);
        this.rowsChanged.emit();
    }

    private setRowsFromAnchors(anchors: ValueDescriptionAnchor[]): void {
        if (this.signalAnchor) {
            this.signalAnchor.valueDescriptionAnchors = anchors;
        }
        this.rows = createRowsFromAnchors(anchors);
    }
}
